import type { FC, ReactNode } from 'react';
import React, { useContext, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { graphql } from 'react-relay';

import AkButton from '@atlaskit/button/custom-theme-button';
import type { Appearance } from '@atlaskit/button/types';
import type { Appearance as NewButtonAppearance } from '@atlaskit/button/new';
import Button from '@atlaskit/button/new';
import EditionsIcon from '@atlaskit/icon-lab/core/editions';
import { token } from '@atlaskit/tokens';

import { ExperimentContext } from '@atlassian/touchpoint-commons-event-analytics';
import {
	createProductSpecificAnalyticsIntegrationPayload,
	ErrorType,
	Severity,
	Product,
	Team,
} from '@atlassian/growth-commons-error-analytics';

import {
	UPSELL_EDITION_PREMIUM_EXPERIENCE,
	UPSELL_EDITION_UPFLOW_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { stopExperienceOnError } from '@confluence/experience-tracker/entry-points/getExperienceTracker';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop';
import { useChangeEditionUpgradeButtonContentExperiment } from '@confluence/experiment-change-edition-upgrade-button-content/entry-points/useChangeEditionUpgradeButtonContentExperiment';
import { componentWithFG } from '@confluence/feature-gate-component';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary';

import { useUpflowV2ConfluenceStandardFGsExperiment } from '../UpflowV2ConfluenceStandardFGsExperiment/services/useUpflowV2ConfluenceStandardFGsExperiment';
import { UP_FLOW_EP_MESSAGE_ID } from '../constants';
import {
	CommerceDeepLinksTargetEdition,
	useCommerceDeepLinks,
} from '../CommerceUtils/useCommerceDeepLinks';
import { GradientCTAButton } from '../GradientLooknFeel/GradientCTAButton';
import { GradientSubtleLinkButton } from '../GradientLooknFeel/GradientSubtleLinkButton';
import { TryStandardUpflowV2ExperimentTreatmentModalLoadable } from '../UpflowV2ConfluenceStandardFGsExperiment/ui/UpflowV2ExperimentTreatmentModalLoadable';
import type { UpflowV2Touchpoint } from '../UpflowV2ConfluenceStandardFGsExperiment/ui/StandardEditionHighlights';

import { ConfluenceEdition } from './__types__/waitForEditionChangeQuery';
import { UpFlowCommerceRouterLoadable } from './UpFlowCommerceRouterLoadable';
import { TimeoutError, waitForEditionChange } from './waitForEditionChange';
import { calculateTestId } from './calculateTestId';
import { useEditionsAnalyticsContext } from './AnalyticsProvider';

export type UpgradeEditionButtonProps = {
	analyticsProps: {
		actionSubjectId: string;
		attributes: any;
		source: string;
	};
	appearance?: Appearance;
	targetEdition?: ConfluenceEdition;
	children?: ReactNode;
	onUpFlowOpen?: Function;
	onUpgradeButtonClick?: Function;
};

const i18n = defineMessages({
	tryItFree: {
		id: 'change-edition.upgrade.try.free',
		defaultMessage: 'Try it free',
		description: 'The text of the button/link which starts the user onto the path of free trial',
	},
	tryItNow: {
		id: 'change-edition.upgrade.try.now',
		defaultMessage: 'Try it now',
		description: 'The text of the button/link which starts the user onto the path of upgrade',
	},
	tryPremium: {
		id: 'change-edition.upgrade.try.premium',
		defaultMessage: 'Try Premium',
		description:
			'The text of the button/link which starts the user onto the path of upgrade to Premium edition',
	},
});

const editionMap: Record<ConfluenceEdition, CommerceDeepLinksTargetEdition> = {
	[ConfluenceEdition.FREE]: CommerceDeepLinksTargetEdition.FREE,
	[ConfluenceEdition.STANDARD]: CommerceDeepLinksTargetEdition.STANDARD,
	[ConfluenceEdition.PREMIUM]: CommerceDeepLinksTargetEdition.PREMIUM,
};

const determineButtonComponent = (appearance: Appearance) => {
	if (appearance === 'primary') {
		return GradientCTAButton;
	}

	if (appearance === 'subtle-link') {
		return GradientSubtleLinkButton;
	}

	return AkButton;
};
/**
 * @deprecated Please reach out to #cc-flywheel-engagement for details before use. Please read https://hello.atlassian.net/wiki/spaces/CE2/pages/2797298237/Contribution+Consumption+Guidelines+For+Common+Feature+Gate+Upsell+Component
 */
const UpgradeEditionButtonImpl: FC<UpgradeEditionButtonProps> = ({
	analyticsProps: { actionSubjectId, attributes, source },
	appearance = 'primary',
	children,
	onUpFlowOpen,
	targetEdition = ConfluenceEdition.STANDARD,
	onUpgradeButtonClick,
}: UpgradeEditionButtonProps) => {
	const [openUpFlow, setOpenUpFlow] = useState<boolean>(false);

	const [pollingError, setPollingError] = useState<Error | undefined>();
	const { cloudId, edition: currentEdition, userId } = useSessionData();
	const { createAnalyticsEvent } = useEditionsAnalyticsContext();
	const writeFragmentInterop = useWriteFragmentInterop({
		fragmentSpec: graphql`
			fragment UpgradeEditionButtonFragment on ConfluenceTenantContext {
				editions {
					confluence
					__typename
				}
				__typename
			}
		`,
	});

	const experienceTracker = useContext(ExperienceTrackerContext) as any;

	// free -> standard: upflow spa;
	// free/standard -> premium: adminhub
	const showUpFlow = targetEdition === ConfluenceEdition.STANDARD;

	const {
		cohort: upflowV2ExperimentCohort,
		isSiteAdmin,
		isBillingAdmin,
		loading: upflowV2EligibilityHookLoading,
	} = useUpflowV2ConfluenceStandardFGsExperiment({
		skip: !showUpFlow || !openUpFlow,
		touchpointId: source as UpflowV2Touchpoint,
		currentEdition,
		targetEdition,
	});

	const {
		commerceBackendName,
		planConsentDeepLink,
		errorMessage: commerceDeepLinksError,
	} = useCommerceDeepLinks({
		targetEdition: editionMap[targetEdition],
		skipDeepLinks: showUpFlow,
	});

	useEffect(
		() => () => {
			// 1. With upflow enabled, when the user clicks on the upgrade CTA, the
			//    upflow dialog opens and this component unmounts; the upsell experience
			//    continues and experience tracking is subsequently handled by the
			//    UpFlow component.
			// or
			// 2. With upflow DISabled, unmounting here simply means user navigated away
			//    from the upsell UI and we DO want to signal an aborted experience.
			if (!showUpFlow) {
				experienceTracker.abort({
					name: UPSELL_EDITION_PREMIUM_EXPERIENCE,
					reason: 'Canceled by user',
				});
			}
		},
		[experienceTracker, showUpFlow],
	);

	useEffect(() => {
		// Using a TRACK event for tacking the traffic to the UpgradeEditionButton.
		// This will help us gauage Commerce API traffic to compute the deep links.
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'rendered',
				actionSubject: 'upgradeEditionButton',
				actionSubjectId,
				source,
				attributes: {
					...attributes,
					currentEdition,
					targetEdition,
				},
			},
		}).fire();
		// We only need this to fire once, when the component mounts
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fireClickAnalytics = () => {
		if (attributes) {
			attributes.currentEdition = currentEdition;
			attributes.targetEdition = targetEdition;
			attributes.commerceBackend = commerceBackendName;
		}

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId,
				source,
				attributes,
			},
		}).fire();

		if (commerceDeepLinksError) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'errored',
					actionSubject: 'useCommerceDeepLinks',
					actionSubjectId,
					source,
					attributes: {
						...attributes,
						currentEdition,
						targetEdition,
						upsellDeepLink: planConsentDeepLink ? planConsentDeepLink.split('?')[0] : undefined,
						errorMessage: commerceDeepLinksError,
					},
				},
			}).fire();
		}
	};

	const onTryWithoutUpFlowClick = async () => {
		if (onUpgradeButtonClick) {
			onUpgradeButtonClick();
		}

		// start polling and reload the page when done
		try {
			const updatedEdition = await waitForEditionChange(currentEdition, writeFragmentInterop);
			experienceTracker.succeed({
				name: UPSELL_EDITION_PREMIUM_EXPERIENCE,
				attributes: {
					currentEdition,
					targetEdition: updatedEdition,
				},
			});

			// Default behavior
			window.location.reload();
		} catch (error) {
			if (error === TimeoutError) {
				// We don't expect the user to always proceed to upgrade in Admin Hub after seeing the estimated billing info.
				experienceTracker.abort({
					name: UPSELL_EDITION_PREMIUM_EXPERIENCE,
					reason: 'Possibly canceled by user',
				});
			} else {
				stopExperienceOnError(UPSELL_EDITION_PREMIUM_EXPERIENCE, error);
				setPollingError(error);
			}
		}
	};

	const onUpFlowClose = () => {
		setOpenUpFlow(false);
	};

	const onTryButtonClick = () => {
		// we always want to fire both the exposure event as well as the click event
		fireClickAnalytics();

		// start the experience tracker
		experienceTracker.start({
			name: showUpFlow ? UPSELL_EDITION_UPFLOW_EXPERIENCE : UPSELL_EDITION_PREMIUM_EXPERIENCE,
			attributes: {
				currentEdition,
				linkClicked: actionSubjectId,
				source,
				targetEdition,
			},
		});

		// then handle the click behaviour based on FF
		if (showUpFlow) {
			setOpenUpFlow(true);
		} else {
			void onTryWithoutUpFlowClick();
		}
	};

	const buttonProps = {
		appearance,
		className: 'primaryButton',
		href: showUpFlow ? undefined : planConsentDeepLink,
		onClick: onTryButtonClick,
		target: showUpFlow ? undefined : '_blank',
		iconBefore:
			appearance === 'primary' ? (
				<EditionsIcon label="" color={token('color.icon.inverse')} />
			) : undefined,
		testId: calculateTestId({ source, actionSubjectId }),
	};

	const upflowV2ExperimentButtonProps = {
		appearance: appearance as NewButtonAppearance,
		testId: 'upflowV2ExperimentLoadingButton',
		isDisabled: true,
		isLoading: true,
		iconBefore: appearance === 'primary' ? EditionsIcon : undefined,
	};

	const buttonChildren = children || <FormattedMessage {...i18n.tryItFree} />;

	const ButtonComponent = determineButtonComponent(appearance);

	const renderModalContent = () => {
		if (upflowV2EligibilityHookLoading) return null;

		if (upflowV2ExperimentCohort === 'variation') {
			return (
				<TryStandardUpflowV2ExperimentTreatmentModalLoadable
					isOpen={openUpFlow}
					closeModal={onUpFlowClose}
					touchpointId={source as UpflowV2Touchpoint}
					targetEdition={targetEdition}
					isBillingAdmin={isBillingAdmin}
					isSiteAdmin={isSiteAdmin}
					currentEdition={currentEdition}
				/>
			);
		}

		return (
			<UpFlowCommerceRouterLoadable
				canChangeEdition // this component should only ever be used for site admins
				cloudId={cloudId}
				currentEdition={currentEdition || undefined}
				atlassianAccountId={userId || undefined}
				flow="upgrade"
				product="confluence"
				targetEdition={targetEdition}
				touchpointId={source}
				isExperienceTrackerEnabled
				onClose={
					// Note: onUpFlowOpen function closes the parent component which
					// means we don't need to trigger the onClose.
					onUpFlowOpen ? undefined : onUpFlowClose
				}
				onOpen={onUpFlowOpen}
				epMessageId={UP_FLOW_EP_MESSAGE_ID}
			/>
		);
	};

	return (
		<>
			{pollingError ? <ErrorDisplay error={pollingError} /> : null}
			{upflowV2EligibilityHookLoading ? (
				<Button {...upflowV2ExperimentButtonProps}>{buttonChildren}</Button>
			) : (
				<ButtonComponent {...buttonProps}>{buttonChildren}</ButtonComponent>
			)}
			{showUpFlow && openUpFlow ? renderModalContent() : null}
		</>
	);
};

const UpgradeEditionButtonWithExperiment: FC<UpgradeEditionButtonProps> = ({
	analyticsProps: { actionSubjectId, attributes, source },
	appearance = 'primary',
	children,
	onUpFlowOpen,
	targetEdition = ConfluenceEdition.STANDARD,
	onUpgradeButtonClick,
}: UpgradeEditionButtonProps) => {
	const { edition } = useSessionData();
	const { createAnalyticsEvent } = useEditionsAnalyticsContext();

	const { upgradeButtonContent, cohort } = useChangeEditionUpgradeButtonContentExperiment({
		targetEdition,
		featureGateId: actionSubjectId || source,
	});

	return (
		<TransparentErrorBoundary
			attribution={Attribution.GROWTH_LUNA}
			onError={(error, info) => {
				const eventPayload = createProductSpecificAnalyticsIntegrationPayload({
					product: Product.CONFLUENCE,
					team: Team.GROWTH_LUNA,
					experimentMeta: {
						experimentKey: 'replace_upsell_cta_content',
					},
					errorType: ErrorType.ERROR_BOUNDARY,
					errorMessage: error.message,
					severity: Severity.MEDIUM,
					source: 'replace_upsell_cta_content',
					additionalAttributes: {
						...info,
						feature_gate: actionSubjectId || source,
						attributes,
						source,
					},
				});

				createAnalyticsEvent(eventPayload).fire();
			}}
		>
			<ExperimentContext
				experimentMeta={{
					key: 'replace_upsell_cta_content',
					cohort,
					targetEdition,
					currentEdition: edition,
					content: upgradeButtonContent || children,
				}}
			>
				<UpgradeEditionButtonImpl
					analyticsProps={{ actionSubjectId, attributes, source }}
					appearance={appearance}
					onUpFlowOpen={onUpFlowOpen}
					targetEdition={targetEdition}
					onUpgradeButtonClick={onUpgradeButtonClick}
				>
					{upgradeButtonContent || children}
				</UpgradeEditionButtonImpl>
			</ExperimentContext>
		</TransparentErrorBoundary>
	);
};

/**
 * @deprecated Please reach out to #growth-luna for details before use. Please read https://hello.atlassian.net/wiki/spaces/CE2/pages/2797298237/Contribution+Consumption+Guidelines+For+Common+Feature+Gate+Upsell+Component
 */
export const UpgradeEditionButton = componentWithFG(
	'replace_upsell_cta_content_fg',
	UpgradeEditionButtonWithExperiment,
	UpgradeEditionButtonImpl,
);
